/** GPU Unsharp Mask — fragment-shader port of
 *  `lib/filters/unsharp-mask.ts`.
 *
 *  Three-pass chain:
 *    Pass 0 (H-Gauss): same as filter-gaussian-blur's horizontal
 *      pass. Imports `GAUSSIAN_HORIZONTAL_FRAG` verbatim so the
 *      blur math can't drift from the standalone Gaussian filter.
 *    Pass 1 (V-Gauss): vertical Gaussian — completes the blurred
 *      copy of the source.
 *    Pass 2 (combine): reads `u_source` (blurred) and `u_original`
 *      (original input, kept on texture unit 1 by the runtime for
 *      the lifetime of the chain), computes per-channel
 *      `out = source + amount * (source - blur)` with the
 *      `|diff| >= threshold` gate that protects flat regions from
 *      having sensor noise amplified.
 *
 *  Identity short-circuits (amount=0 OR radius=0) match the JS
 *  path via `applyIdentityCopy`. */

import { buildGaussianKernel1D } from '../../filters/gaussian-blur';
import type { UnsharpMaskValue } from '../../filters/unsharp-mask';
import { MAX_KERNEL_RADIUS } from '../blur-shared';
import { applyIdentityCopy } from '../identity-copy';
import { applyShaderPasses } from '../runtime';
import {
  GAUSSIAN_HORIZONTAL_FRAG,
  GAUSSIAN_VERTICAL_FRAG,
  padGaussianKernel,
} from './gaussian-blur';

/** Combine pass: read original and blurred, output sharpened
 *  result. Threshold comparison happens in 0..1 normalized space
 *  (`u_threshold` is pre-scaled CPU-side from 0..255 to 0..1 so the
 *  comparison matches the JS path's `|diff| >= threshold` in
 *  0..255 space). Alpha is copied from `u_original` to match the
 *  JS path's `out[i+3] = src[i+3]`. */
const COMBINE_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform sampler2D u_original;
uniform float u_k;
uniform float u_threshold;
out vec4 outColor;

void main() {
  vec4 src = texture(u_original, v_uv);
  vec3 blur = texture(u_source, v_uv).rgb;
  vec3 diff = src.rgb - blur;
  // Per-channel threshold gate. abs(diff) < threshold → pass through
  // the source unchanged; otherwise add k * diff. Matches JS's
  // per-channel decision in unsharp-mask.ts.
  vec3 mask = step(vec3(u_threshold), abs(diff));
  vec3 sharpened = src.rgb + u_k * diff * mask;
  outColor = vec4(sharpened, src.a);
}
`;

export function applyUnsharpMaskOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: UnsharpMaskValue,
): boolean {
  const amount = Math.max(0, value.amount);
  const radius = Math.max(0, value.radius);
  if (amount === 0 || radius === 0) return applyIdentityCopy(source, target);
  const radiusInt = Math.floor(radius);
  if (radiusInt > MAX_KERNEL_RADIUS) return false;
  const threshold = Math.max(0, Math.min(255, value.threshold));
  const k = amount / 100;
  const kernel = buildGaussianKernel1D(radiusInt);
  const paddedKernel = padGaussianKernel(kernel);
  const dims: [number, number] = [source.width, source.height];
  return applyShaderPasses({
    source,
    target,
    passes: [
      {
        fragShaderSrc: GAUSSIAN_HORIZONTAL_FRAG,
        uniforms: {
          u_dims: { type: 'vec2', value: dims },
          u_radius: { type: 'int', value: radiusInt },
          u_kernel: { type: 'float[]', value: paddedKernel },
        },
      },
      {
        fragShaderSrc: GAUSSIAN_VERTICAL_FRAG,
        uniforms: {
          u_dims: { type: 'vec2', value: dims },
          u_radius: { type: 'int', value: radiusInt },
          u_kernel: { type: 'float[]', value: paddedKernel },
        },
      },
      {
        fragShaderSrc: COMBINE_FRAG,
        uniforms: {
          u_k: { type: 'float', value: k },
          // Threshold is 0..255 in the CPU path; normalize to 0..1
          // so the shader's `step(threshold, abs(diff))` operates on
          // normalized texture samples.
          u_threshold: { type: 'float', value: threshold / 255 },
        },
      },
    ],
  });
}
